/** Device configuration settings stored in IndexedDB */

import { openDatabase } from '@tunasong/indexeddb-storage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// In-memory cache so remounts get persisted values synchronously
const valueCache = new Map<string, unknown>()

// Event emitter for cross-component updates
const dbEventEmitter = {
  listeners: new Set<(key: string, value: unknown) => void>(),
  subscribe(callback: (name: string, value: unknown) => void) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  },
  emit<T>(key: string, value: T) {
    for (const callback of this.listeners) {
      callback(key, value)
    }
  },
}

/**
 * Store a named state on a device IndexedDB database.
 *
 * The value is loaded async from IndexedDB, so if you need to wait for the value to be loaded,
 * set the `defaultValue` and wait to render until val is available.
 */
export const useDeviceState = <T>(name: string, defaultValue?: T, nameSpace = 'device') => {
  const db = useMemo(() => openDatabase<T>(nameSpace), [nameSpace])
  const cacheKey = `${nameSpace}:${name}`
  const [val, setVal] = useState<T | undefined>(
    valueCache.has(cacheKey) ? (valueCache.get(cacheKey) as T) : defaultValue
  )
  const [ready, setReady] = useState(valueCache.has(cacheKey))

  const lastName = useRef<string>(undefined)
  const defaultValueRef = useRef(defaultValue)
  defaultValueRef.current = defaultValue

  // Track mount so the IndexedDB `db.get(...)` Promise (which can resolve
  // after the component is unmounted) never calls setState on a dead fiber.
  // React 19's scheduler reads `window` from `resolveUpdatePriority` when
  // dispatching a state update; in test teardown jsdom has already removed
  // `window`, so a late setState surfaces as an unhandled
  // `ReferenceError: window is not defined`. Gate every setState on the ref.
  // Set it again on mount: StrictMode mounts, unmounts and remounts, and a ref left false
  // would drop every update from other components using the same key.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const unsubscribe = dbEventEmitter.subscribe((key, value) => {
      if (key !== name) {
        return
      }
      if (!isMountedRef.current) {
        return
      }
      setVal(value as T)
    })

    return () => {
      unsubscribe()
    }
  }, [name])
  useEffect(() => {
    if (!db) {
      return
    }

    // Load the initial value
    void db.get(name).then(val => {
      if (!isMountedRef.current) {
        return
      }
      const resolved = val ?? defaultValueRef.current
      valueCache.set(cacheKey, resolved)
      setVal(resolved)
      lastName.current = name
      setReady(true)
    })
  }, [db, name, cacheKey])

  const set = useCallback(
    (value: T) => {
      valueCache.set(cacheKey, value)
      setVal(value)
      void db?.set(name, value)
      dbEventEmitter.emit(name, value)
    },
    [db, name, cacheKey]
  )

  return [val, set, ready] as [T | undefined, typeof set, boolean]
}
